import { AttachFile, Download, Help } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
	Backdrop,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormHelperText,
	IconButton,
	Stack,
	Tooltip,
	Typography,
} from '@mui/material';
import { get } from 'lodash';
import React, { FC, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import {
	FILE_UPLOAD_ALLOWED_FORMATS,
	FILE_UPLOAD_ALLOWED_FORMATS_ONLY_IMAGES,
	FILE_UPLOAD_ALLOWED_SIZE,
	INVALID_FILE_UPLOAD_TEXT_MESSAGE,
	INVALID_ONLY_IMAGES_UPLOAD_TEXT_MESSAGE,
} from '../../../../../lib/constants/constant';
import { STRAPI_URL } from '../../../../../lib/constants/url';
import { MUIFieldProps } from '../../../../../lib/types/types';
import { DisplayFileSize, GetFileSize } from '../../../../../utils/formats';

import MUIToolTip from 'src/components/ui-component/mui-tooltip/MUIToolTip';
import { includesAnyNormalized } from 'src/utils/helper';
import { mimeToName } from 'src/utils/uploadHelper';
import { DeleteFile, PostFile } from '../../../../../lib/api/files';
import LoadingBackdrop from '../../../loading/LoadingBackdrop';

const SingleUpload: FC<MUIFieldProps> = (props) => {
	const { label, fieldValue, updateFieldValue, isDisabled, errorMessage, allowOnlyImage, allowedFileNameKeywords, allowedFormats } = props;
	const maxAllowedFileSize = get(props, 'maxFileSize', FILE_UPLOAD_ALLOWED_SIZE);
	const hinweisText = get(props, 'uploadHinweisText');
	const [imageSrc, setImageSrc] = useState<any>(null);
	const [showImage, setShowImage] = useState(false);

	const [deleteOpen, setDeleteOpen] = useState(-1);

	const [tooBigFiles, setTooBigFiles] = useState<any>('');
	const [invalidFileFormat, setInvalidFileFormat] = useState<any>('');
	const [webserviceActive, setWebserviceActive] = useState<boolean>(false);

	const toolTip = get(props, 'tooltip', '');
	// Determine allowed formats: custom prop, then allowOnlyImage, then default
	const ALLOWED_FORMATS =
		allowedFormats && allowedFormats.length > 0 ? allowedFormats
		: allowOnlyImage ? FILE_UPLOAD_ALLOWED_FORMATS_ONLY_IMAGES
		: FILE_UPLOAD_ALLOWED_FORMATS;

	const handleShowImagePreview = async (url: string, fileType: string) => {
		if (fileType.includes('image')) fetchFile(url);
		else; // Do Nothing
	};
	const handleDownloadFile = async (url: string, fileName: string) => {
		await fetchFile(url, true, fileName);
	};
	const fetchFile = async (url: string, download: boolean = false, fileName: string = '') => {
		try {
			setWebserviceActive(true);
			// Private file URLs need the /api/files prefix so the accessToken cookie (path: '/api') is sent
			const fileUrl = url.startsWith('/private/') ? `${STRAPI_URL}/api/files${url}` : `${STRAPI_URL}${url}`;
			let res = await fetch(fileUrl, {
				credentials: 'include',
			});

			const blob = await res.blob();
			const objectUrl = URL.createObjectURL(blob);
			if (download) {
				const link = document.createElement('a');
				link.href = objectUrl;
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(objectUrl);
			} else {
				setImageSrc(objectUrl);
			}
		} catch (err: any) {
			console.error(' ERROR: Downloading File ', err);
		} finally {
			if (!download) setShowImage(true);
			setWebserviceActive(false);
		}
	};

	const validateFile = (file: any) => {
		// If custom allowedFormats prop is set, check against it
		if (allowedFormats && allowedFormats.length > 0) {
			if (!allowedFormats.includes(file.type)) {
				const allowedNames = allowedFormats.map(mimeToName).join(', ');
				setInvalidFileFormat(
					allowedFormats.length > 1 ?
						`Ungültiges Dateiformat. Erlaubte Formate sind: ${allowedNames}.`
					:	`Ungültiges Dateiformat. Erlaubtes Format ist: ${allowedNames}.`,
				);
				return false;
			}
		} else {
			// Fallback to default logic
			if (!ALLOWED_FORMATS.includes(file.type)) {
				setInvalidFileFormat(allowOnlyImage ? INVALID_ONLY_IMAGES_UPLOAD_TEXT_MESSAGE : INVALID_FILE_UPLOAD_TEXT_MESSAGE);
				return false;
			}
		}

		if (file.size > maxAllowedFileSize) {
			setTooBigFiles(file);
			return false;
		}
		if (allowedFileNameKeywords && allowedFileNameKeywords.length > 0) {
			if (!includesAnyNormalized(file.name, allowedFileNameKeywords)) {
				setInvalidFileFormat(`Die Datei '${file.name}' ist ungültig. Sie muss '${allowedFileNameKeywords}' im Namen enthalten.`);
				return false;
			}
		}
		return true;
	};

	const handleUploadSingleFile = async (file: any) => {
		if (validateFile(file)) {
			try {
				setWebserviceActive(true);
				let res = await PostFile(file);
				let e = { target: { value: { id: get(res, '0.id'), attributes: res[0] } } };
				updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
			} catch (err: any) {
				console.error('ERROR: Uploading FILE ', err);
			} finally {
				setWebserviceActive(false);
			}
		}
	};

	const handleDeleteFile = async () => {
		let fileId = get(fieldValue, `id`, '');
		if (fileId) {
			setWebserviceActive(true);
			DeleteFile(fileId).finally(() => setWebserviceActive(false));
		}
		let e = { target: { value: null } };
		updateFieldValue(e as unknown as React.ChangeEvent<HTMLInputElement>);
	};

	return (
		<>
			<LoadingBackdrop open={webserviceActive} />
			<Backdrop open={showImage} onClick={() => setShowImage(false)} sx={{ zIndex: 999 }}>
				<img src={`${imageSrc}`} alt='Vorschau Bild' width={600} />
			</Backdrop>
			<Dialog open={deleteOpen >= 0} onClose={() => setDeleteOpen(-1)} aria-labelledby={'delete-file-dialog'}>
				<DialogTitle id={'delete-file-dialog'}>Datei löschen</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Möchten Sie die Datei <b>{get(fieldValue, `${deleteOpen}.attributes.name`)}</b> löschen?
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button variant='outlined' onClick={() => setDeleteOpen(-1)} color='primary'>
						Abbrechen
					</Button>
					<Button
						variant='contained'
						onClick={() => {
							handleDeleteFile();
							setDeleteOpen(-1);
						}}
						color='error'
					>
						Löschen
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={get(tooBigFiles, 'name', '').length > 0} onClose={() => setTooBigFiles('')} aria-labelledby={'too-big-file-dialog'}>
				<DialogTitle id={'too-big-file-dialog'}>Datei zu groß</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Die hochgeladene Datei <strong>{'"' + get(tooBigFiles, 'name', '') + '"'}</strong> überschreitet die zulässige Größenbeschränkung (
						{DisplayFileSize(maxAllowedFileSize)}) und daher verworfen. <br />
						Bitte laden Sie eine kleinere Datei hoch oder reduzieren Sie die Dateigröße und versuchen Sie es erneut.
					</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center' }}>
					<Button variant='outlined' onClick={() => setTooBigFiles('')}>
						Schließen
					</Button>
				</DialogActions>
			</Dialog>
			<Dialog open={invalidFileFormat !== '' ? true : false} onClose={() => setInvalidFileFormat('')} aria-labelledby={'invalid-file-dialog'}>
				<DialogTitle id={'invalid-file-dialog'}>Ungültiges Dateiformat</DialogTitle>
				<DialogContent sx={{ textAlign: 'left' }}>
					<DialogContentText>{invalidFileFormat}</DialogContentText>
				</DialogContent>
				<DialogActions sx={{ justifyContent: 'center' }}>
					<Button variant='outlined' onClick={() => setInvalidFileFormat('')}>
						Schließen
					</Button>
				</DialogActions>
			</Dialog>

			{(label || toolTip) && (
				<Box sx={{ display: 'flex', alignItems: 'center', mt: '1rem' }}>
					{label && <Typography sx={{ fontWeight: 600 }}>{label}</Typography>}
					{toolTip && (
						<MUIToolTip title={toolTip}>
							<Help fontSize='small' color='primary' sx={{ ml: '5px' }} />
						</MUIToolTip>
					)}
				</Box>
			)}
			{get(fieldValue, 'attributes.name') ?
				<>
					<Box
						sx={{
							border: `${errorMessage ? '2px dashed #f00' : '2px dashed #00818B'}`,
							borderRadius: '9px',
							padding: '0.2rem 1rem',
							cursor: 'pointer',
						}}
					>
						<Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'} spacing={1}>
							<Stack
								direction={'row'}
								spacing={1}
								alignItems={'center'}
								sx={{
									minWidth: 0,
									flex: 1,
									overflow: 'hidden',
								}}
							>
								<Typography
									variant='body2'
									component={'div'}
									onClick={() => handleShowImagePreview(get(fieldValue, 'attributes.url'), get(fieldValue, 'attributes.mime'))}
									sx={{
										overflow: 'hidden',
										textOverflow: 'ellipsis',
										whiteSpace: 'nowrap',
										minWidth: 0,
									}}
								>
									<Tooltip title={get(fieldValue, 'attributes.name', '')} placement='top' arrow>
										<span>{get(fieldValue, 'attributes.name', '')}</span>
										{/* <>
											{get(fieldValue, 'attributes.name', '').length > 40 ?
												get(fieldValue, 'attributes.name', '').slice(0, 34) + '...' + get(fieldValue, 'attributes.name', '').slice(-4)
											:	get(fieldValue, 'attributes.name', '')}
										</> */}
									</Tooltip>
								</Typography>
								<Typography variant='body2' component={'div'}>
									{'-'}
								</Typography>
								<Typography variant='body2' component={'div'} sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
									{GetFileSize(get(fieldValue, 'attributes.size'))}
								</Typography>
							</Stack>
							<Stack direction={'row'} sx={{ flexShrink: 0 }}>
								{get(fieldValue, 'attributes.url') && (
									<IconButton
										onClick={() => handleDownloadFile(get(fieldValue, 'attributes.url'), get(fieldValue, 'attributes.name'))}
										size='small'
									>
										<Download />
									</IconButton>
								)}

								<IconButton disabled={isDisabled} onClick={() => setDeleteOpen(1)} size='small'>
									<DeleteIcon />
								</IconButton>
							</Stack>
						</Stack>
					</Box>
					{hinweisText && <Typography sx={{ fontSize: '0.875rem' }}>{hinweisText}</Typography>}
				</>
			:	<>
					{!isDisabled ?
						<>
							<FileUploader multiple={false} handleChange={handleUploadSingleFile} disabled={isDisabled}>
								<Box
									sx={{
										border: `${errorMessage ? '2px dashed #f00' : '2px dashed #00818B'}`,
										borderRadius: '9px',
										padding: '1.1rem',
										cursor: 'pointer',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<AttachFile sx={{ width: '3rem', height: '1rem' }} />
									<Typography variant='body2' component={'div'}>
										Datei auswählen oder hierher ziehen
									</Typography>
								</Box>
							</FileUploader>
							{errorMessage ?
								<>
									<FormHelperText error sx={{ marginLeft: '1rem' }}>
										{errorMessage}
									</FormHelperText>
									{hinweisText && <Typography sx={{ fontSize: '0.875rem' }}>{hinweisText}</Typography>}
								</>
							:	hinweisText && <Typography sx={{ fontSize: '0.875rem' }}>{hinweisText}</Typography>}
						</>
					:	<Box
							sx={{
								border: `${errorMessage ? '2px dashed #f00' : '2px dashed #00818B'}`,
								borderRadius: '9px',
								padding: '1.1rem',
								cursor: 'pointer',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
							}}
						>
							<Typography>Es wurde kein Dokument hochgeladen.</Typography>
							{hinweisText && <Typography>{hinweisText}</Typography>}
						</Box>
					}
				</>
			}
		</>
	);
};
export default SingleUpload;
